import React, { useState, useEffect } from 'react';
import { LogEntry, LogLevel } from '../types';
import { apiService } from '../services/api';

export const ErrorLogs: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<LogLevel | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Load error logs
  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await apiService.getErrorLogs();
      setLogs(response.logs);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  // Clear logs
  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all logs? This action cannot be undone.')) {
      return;
    }
    
    try {
      await apiService.clearErrorLogs();
      await loadLogs(); // Refresh the list
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear logs');
    }
  };

  // Generate test logs
  const handleGenerateTestLogs = async () => {
    try {
      await apiService.generateTestLogs();
      await loadLogs(); // Refresh the list
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate test logs');
    }
  };

  // Filter logs based on level and search term
  const filteredLogs = logs.filter(log => {
    const matchesLevel = filter === 'all' || log.level === filter;
    const matchesSearch = searchTerm === '' || 
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.component?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.error?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesLevel && matchesSearch;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredLogs.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
  };

  // Load logs on mount and set up auto-refresh
  useEffect(() => {
    loadLogs();
    
    if (autoRefresh) {
      const interval = setInterval(loadLogs, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  // Get log level badge styling
  const getLevelBadgeClass = (level: LogLevel) => {
    switch (level) {
      case 'ERROR':
        return 'log-level-error';
      case 'WARN':
        return 'log-level-warn';
      case 'INFO':
        return 'log-level-info';
      case 'DEBUG':
        return 'log-level-debug';
      default:
        return 'log-level-default';
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  // Get log count by level
  const getLogCounts = () => {
    const counts = {
      ERROR: logs.filter(l => l.level === 'ERROR').length,
      WARN: logs.filter(l => l.level === 'WARN').length,
      INFO: logs.filter(l => l.level === 'INFO').length,
    };
    return counts;
  };

  const logCounts = getLogCounts();

  return (
    <div className="error-logs-dashboard">
      <div className="logs-controls-section">
        <h2>System Logs</h2>
        <p className="section-description">
          Monitor system errors, warnings, and important events. Logs are automatically refreshed every 10 seconds.
        </p>
        
        <div className="logs-controls">
          <div className="logs-filters">
            <div className="filter-group">
              <label htmlFor="levelFilter">Filter by Level:</label>
              <select
                id="levelFilter"
                value={filter}
                onChange={(e) => setFilter(e.target.value as LogLevel | 'all')}
                className="level-filter-select"
              >
                <option value="all">All Levels ({logs.length})</option>
                <option value="ERROR">Errors ({logCounts.ERROR})</option>
                <option value="WARN">Warnings ({logCounts.WARN})</option>
                <option value="INFO">Info ({logCounts.INFO})</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="searchLogs">Search Logs:</label>
              <input
                id="searchLogs"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search messages, components, errors..."
                className="search-input"
              />
            </div>
          </div>
          
          <div className="logs-actions">
            <label className="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>
            
            <button 
              onClick={loadLogs} 
              disabled={loading}
              className="refresh-button"
            >
              🔄 Refresh
            </button>
            
            <button 
              onClick={handleGenerateTestLogs}
              className="generate-test-button"
            >
              🧪 Generate Test Logs
            </button>
            
            <button 
              onClick={handleClearLogs}
              className="clear-logs-button"
            >
              🗑️ Clear Logs
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="logs-content-section">
        <div className="section-header">
          <div className="logs-header-left">
            <h3>
              Log Entries 
              {filteredLogs.length !== logs.length && (
                <span className="filter-count">({filteredLogs.length} of {logs.length})</span>
              )}
            </h3>
            
            <div className="pagination-info">
              {filteredLogs.length > 0 && (
                <span className="pagination-summary">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredLogs.length)} of {filteredLogs.length}
                </span>
              )}
            </div>
          </div>
          
          <div className="logs-header-right">
            <div className="log-summary">
              <span className="log-count error">🔴 {logCounts.ERROR}</span>
              <span className="log-count warn">🟡 {logCounts.WARN}</span>
              <span className="log-count info">🔵 {logCounts.INFO}</span>
            </div>
            
            <div className="page-size-selector">
              <label htmlFor="pageSize">Per page:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="page-size-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>

        {loading && logs.length === 0 ? (
          <div className="loading">Loading logs...</div>
        ) : filteredLogs.length === 0 ? (
          <div className="no-logs">
            <div className="empty-state">
              <h3>No logs found</h3>
              <p>
                {logs.length === 0 
                  ? 'No logs have been recorded yet.' 
                  : 'No logs match your current filters.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="logs-list">
              {paginatedLogs.map((log, index) => (
                <div key={`${log.timestamp}-${index}`} className={`log-entry ${log.level.toLowerCase()}`}>
                  <div className="log-header">
                    <div className="log-meta">
                      <span className={`log-level-badge ${getLevelBadgeClass(log.level)}`}>
                        {log.level}
                      </span>
                      <span className="log-timestamp">
                        {formatTimestamp(log.timestamp)}
                      </span>
                      {log.component && (
                        <span className="log-component">
                          {log.component}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="log-content">
                    <div className="log-message">
                      {log.message}
                    </div>
                    
                    {log.error && (
                      <div className="log-error">
                        <strong>Error:</strong> {log.error}
                      </div>
                    )}
                    
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="log-details">
                        <strong>Details:</strong>
                        <pre className="log-details-json">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  ← Previous
                </button>
                
                <div className="pagination-pages">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                    // Show first page, last page, current page, and pages around current
                    const showPage = page === 1 || 
                                   page === totalPages || 
                                   Math.abs(page - currentPage) <= 1;
                    
                    if (!showPage && page === 2 && currentPage > 4) {
                      return <span key={page} className="pagination-ellipsis">...</span>;
                    }
                    
                    if (!showPage && page === totalPages - 1 && currentPage < totalPages - 3) {
                      return <span key={page} className="pagination-ellipsis">...</span>;
                    }
                    
                    if (!showPage) return null;
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`pagination-button ${page === currentPage ? 'active' : ''}`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};